import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import BookingModal from '../booking/BookingModal';
import BookingSuccess from '../booking/BookingSuccess';
import { navigateToMainPageSection } from '../../utils/scrollUtils';
import './ClassPage.css';

const ClassPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  const [yogaClass, setYogaClass] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parsedBenefits, setParsedBenefits] = useState([]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState({});

  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingData, setBookingData] = useState(null);

  const [copyFeedback, setCopyFeedback] = useState(false);
  const touchStartX = useRef(null);

  // Fetch class data
  useEffect(() => {
    const fetchClass = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`classes/${id}`);
        const data = response.data;
        setYogaClass(data);

        // Parse benefits
        if (data.benefits) {
          try {
            if (Array.isArray(data.benefits)) {
              setParsedBenefits(data.benefits);
            } else {
              const arr = JSON.parse(data.benefits);
              setParsedBenefits(Array.isArray(arr) ? arr : [data.benefits]);
            }
          } catch {
            const arr = typeof data.benefits === 'string'
              ? data.benefits.split(',').map(s => s.trim()).filter(Boolean)
              : [];
            setParsedBenefits(arr.length ? arr : [data.benefits]);
          }
        }
      } catch (err) {
        setError(t('class_not_found') || 'Class not found');
      } finally {
        setLoading(false);
      }
    };
    fetchClass();
  }, [id, t]);

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return timeStr;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getDifficultyText = (difficulty) => {
    const d = parseInt(difficulty, 10);
    if (d === 1) return t('oddych');
    if (d === 3) return t('makacka');
    return t('normal');
  };

  const getDifficultyDots = (difficulty) => {
    const d = parseInt(difficulty, 10) || 2;
    return [1, 2, 3].map(i => (
      <span key={i} className={`cp-diff-dot ${i <= d ? 'active' : ''}`} />
    ));
  };

  const handleImageError = (index) => {
    setImageErrors(prev => ({ ...prev, [index]: true }));
  };

  const validImages = (yogaClass?.images || []).filter((_, i) => !imageErrors[i]);

  // Touch swipe for gallery
  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) setActiveImageIndex(i => Math.min(i + 1, validImages.length - 1));
      else setActiveImageIndex(i => Math.max(i - 1, 0));
    }
    touchStartX.current = null;
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 2500);
  };

  const handleBookNow = () => {
    if (!yogaClass) return;
    const classDate = new Date(yogaClass.date);
    const [hours, minutes] = yogaClass.start_time.split(':').map(Number);
    classDate.setHours(hours, minutes, 0, 0);
    const classWithDateTime = {
      ...yogaClass,
      date_time: classDate.toISOString(),
      start_time: classDate.toISOString(),
    };
    setYogaClass(classWithDateTime);
    setShowBookingModal(true);
  };

  const handleBookingSuccess = (data) => {
    setShowBookingModal(false);
    setBookingData(data);
    setBookingSuccess(true);
  };

  const isClassInPast = () => {
    if (!yogaClass) return false;
    try {
      const [h, m] = yogaClass.end_time.split(':').map(Number);
      const classEnd = new Date(yogaClass.date);
      classEnd.setHours(h, m, 0, 0);
      return classEnd < new Date();
    } catch { return false; }
  };

  const isClassFull = () => yogaClass && yogaClass.available_spots <= 0;

  if (loading) {
    return (
      <div className="cp-page">
        <div className="cp-left-ellipse cp-decoration" />
        <div className="cp-right-ellipse cp-decoration" />
        <div className="cp-loading">
          <div className="cp-spinner" />
          <p>{t('loading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  if (error || !yogaClass) {
    return (
      <div className="cp-page">
        <div className="cp-left-ellipse cp-decoration" />
        <div className="cp-right-ellipse cp-decoration" />
        <div className="cp-error">
          <div className="cp-error-icon">✕</div>
          <h2>{error || t('class_not_found') || 'Class not found'}</h2>
          <button className="cp-btn-primary" onClick={() => navigateToMainPageSection('schedule', 500)}>
            {t('back_to_home') || 'Back to Home'}
          </button>
        </div>
      </div>
    );
  }

  const isPast = isClassInPast();
  const isFull = isClassFull();
  const canBook = !isPast && !isFull && yogaClass.instructor_status !== 'inactive';

  return (
    <div className="cp-page">
      {/* Background decorations */}
      <div className="cp-left-ellipse cp-decoration" />
      <div className="cp-right-ellipse cp-decoration" />

      <div className="cp-container">

        {/* ── Top navigation bar ── */}
        <div className="cp-topbar">
          <button className="cp-back-btn" onClick={() => navigateToMainPageSection('schedule', 500)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            {t('back') || 'Back'}
          </button>

          <button className="cp-share-btn" onClick={handleShare}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            {copyFeedback ? (t('link_copied') || 'Link copied!') : (t('share') || 'Share')}
          </button>
        </div>

        {/* ── Hero: class name + key meta ── */}
        <header className="cp-hero">
          <div className="cp-hero-eyebrow">
            <span className="cp-eyebrow-pill">{formatDate(yogaClass.date)}</span>
            <span className="cp-eyebrow-pill cp-time-pill">
              {formatTime(yogaClass.start_time)} – {formatTime(yogaClass.end_time)}
            </span>
          </div>
          <h1 className="cp-title">{yogaClass.title || yogaClass.class_name}</h1>
          <div className="cp-hero-divider" />
        </header>

        {/* ── Main content grid ── */}
        <div className="cp-body">

          {/* ── LEFT COLUMN ── */}
          <div className="cp-left">

            {/* Image gallery */}
            {validImages.length > 0 && (
              <div className="cp-gallery">
                <div
                  className="cp-gallery-main"
                  onTouchStart={handleTouchStart}
                  onTouchEnd={handleTouchEnd}
                >
                  <img
                    key={activeImageIndex}
                    src={validImages[activeImageIndex]}
                    alt={`${yogaClass.title} ${activeImageIndex + 1}`}
                    className="cp-gallery-img"
                    onError={() => handleImageError(activeImageIndex)}
                  />
                  {validImages.length > 1 && (
                    <>
                      <button
                        className="cp-gallery-nav cp-gallery-prev"
                        onClick={() => setActiveImageIndex(i => Math.max(i - 1, 0))}
                        disabled={activeImageIndex === 0}
                        aria-label="Previous image"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="15 18 9 12 15 6" />
                        </svg>
                      </button>
                      <button
                        className="cp-gallery-nav cp-gallery-next"
                        onClick={() => setActiveImageIndex(i => Math.min(i + 1, validImages.length - 1))}
                        disabled={activeImageIndex === validImages.length - 1}
                        aria-label="Next image"
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </button>
                      <div className="cp-gallery-dots">
                        {validImages.map((_, i) => (
                          <button
                            key={i}
                            className={`cp-gallery-dot ${i === activeImageIndex ? 'active' : ''}`}
                            onClick={() => setActiveImageIndex(i)}
                            aria-label={`Image ${i + 1}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Thumbnails for 2-3 images */}
                {validImages.length > 1 && (
                  <div className="cp-gallery-thumbs">
                    {validImages.map((src, i) => (
                      <button
                        key={i}
                        className={`cp-thumb ${i === activeImageIndex ? 'active' : ''}`}
                        onClick={() => setActiveImageIndex(i)}
                      >
                        <img src={src} alt={`Thumb ${i + 1}`} onError={() => handleImageError(i)} />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            {yogaClass.description && (
              <div className="cp-card cp-description-card">
                <h3 className="cp-card-title">{t('description') || 'Description'}</h3>
                <p className="cp-description-text">{yogaClass.description}</p>
              </div>
            )}

            {/* Benefits */}
            {parsedBenefits.length > 0 && (
              <div className="cp-card cp-benefits-card">
                <h3 className="cp-card-title">{t('benefits') || 'Benefits'}</h3>
                <ul className="cp-benefits-list">
                  {parsedBenefits.map((b, i) => (
                    <li key={i} className="cp-benefit-item">
                      <span className="cp-benefit-check">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* ── RIGHT COLUMN ── */}
          <div className="cp-right">

            {/* Instructor card */}
            <div className="cp-card cp-instructor-card">
              <div className="cp-instructor-inner">
                {yogaClass.instructor_image && (
                  <div className="cp-instructor-img-wrap">
                    <img
                      src={yogaClass.instructor_image}
                      alt={yogaClass.instructor}
                      className="cp-instructor-img"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
                <div className="cp-instructor-info">
                  <span className="cp-instructor-label">{t('instructor') || 'Instructor'}</span>
                  <span className="cp-instructor-name">{yogaClass.instructor}</span>
                </div>
              </div>
            </div>

            {/* Class details */}
            <div className="cp-card cp-details-card">
              <h3 className="cp-card-title">{t('class_details') || 'Class Details'}</h3>
              <dl className="cp-details-list">
                <div className="cp-detail-row">
                  <dt>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                    {t('duration') || 'Duration'}
                  </dt>
                  <dd>{yogaClass.duration} min</dd>
                </div>
                <div className="cp-detail-row">
                  <dt>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 0 1-2.827 0l-4.244-4.243a8 8 0 1 1 11.314 0z"/><circle cx="12" cy="11" r="3"/>
                    </svg>
                    {t('location') || 'Location'}
                  </dt>
                  <dd>{yogaClass.location || 'Main Studio'}</dd>
                </div>
                <div className="cp-detail-row">
                  <dt>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                    </svg>
                    {t('difficulty') || 'Difficulty'}
                  </dt>
                  <dd className="cp-difficulty-val">
                    <span className="cp-diff-label">{getDifficultyText(yogaClass.difficulty)}</span>
                    <span className="cp-diff-dots">{getDifficultyDots(yogaClass.difficulty)}</span>
                  </dd>
                </div>
                <div className="cp-detail-row">
                  <dt>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                    {t('available_spots') || 'Spots'}
                  </dt>
                  <dd>
                    <span className={`cp-spots ${isFull ? 'full' : ''}`}>
                      {isFull ? (t('class_full') || 'Full') : `${yogaClass.available_spots} / ${yogaClass.max_capacity}`}
                    </span>
                  </dd>
                </div>
                <div className="cp-detail-row cp-price-row">
                  <dt>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                    </svg>
                    {t('price') || 'Price'}
                  </dt>
                  <dd className="cp-price">{yogaClass.price || 0} <span>EUR</span></dd>
                </div>
              </dl>
            </div>

            {/* Payment methods */}
            <div className="cp-card cp-payment-card">
              <h3 className="cp-card-title">{t('payment_methods') || 'Payment Methods'}</h3>
              <div className="cp-payment-row">
                <div className="cp-payment-item">
                  <img src={require('./payment_methods/multisport.jpg')} alt="MultiSport" onError={(e) => { e.target.style.display='none'; }} />
                  <span>MultiSport</span>
                </div>
                <div className="cp-payment-item">
                  <img src={require('./payment_methods/upbalance.jpg')} alt="UpBalansea" onError={(e) => { e.target.style.display='none'; }} />
                  <span>UpBalansea</span>
                </div>
                <div className="cp-payment-item cp-payment-icon-item">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm.31 14.71V18h-1.48v-1.32c-1.27-.26-2.35-1.04-2.43-2.41h1.44c.07.73.61 1.3 1.98 1.3 1.48 0 1.81-.74 1.81-1.2 0-.63-.33-1.22-2.03-1.63-1.91-.46-3.21-1.23-3.21-2.79 0-1.3.99-2.15 2.44-2.44V6.5h1.48v1.05c1.32.32 2.05 1.24 2.1 2.48h-1.43c-.05-.77-.52-1.31-1.79-1.31-1.2 0-1.92.54-1.92 1.36 0 .79.62 1.3 2.1 1.67 1.48.37 3.14.99 3.14 2.77 0 1.32-1.01 2.28-2.6 2.59z"/>
                  </svg>
                  <span>Cash</span>
                </div>
                <div className="cp-payment-item cp-payment-icon-item">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
                    <path d="M20 6h-2.18c.11-.31.18-.65.18-1 0-1.66-1.34-3-3-3-1.05 0-1.96.54-2.5 1.35l-.5.67-.5-.68C10.96 2.54 10.05 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z"/>
                  </svg>
                  <span>Gift Card</span>
                </div>
              </div>
            </div>

            {/* CTA buttons */}
            <div className="cp-cta">
              {isPast ? (
                <div className="cp-past-notice">
                  {t('class_started') || 'This class has already taken place.'}
                </div>
              ) : (
                <button
                  className={`cp-btn-primary cp-book-btn ${!canBook ? 'disabled' : ''}`}
                  onClick={canBook ? handleBookNow : undefined}
                  disabled={!canBook}
                >
                  {isFull ? (t('class_full') || 'Class Full') : (t('book_now') || 'Book Now')}
                </button>
              )}
              <button
                className="cp-btn-secondary cp-schedule-btn"
                onClick={() => navigateToMainPageSection('schedule', 500)}
              >
                {t('view_schedule') || 'View Full Schedule'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showBookingModal && yogaClass && (
        <BookingModal
          yogaClass={yogaClass}
          onClose={() => setShowBookingModal(false)}
          onSuccess={handleBookingSuccess}
        />
      )}

      {/* Booking Success */}
      {bookingSuccess && (
        <BookingSuccess
          bookingData={bookingData}
          onClose={() => setBookingSuccess(false)}
        />
      )}
    </div>
  );
};

export default ClassPage;
